'use client';

import React from 'react';

import { DepopFlameOutline } from '@depop/web-thrift-ui/Icons/DepopFlameOutline';
import { Selling } from '@depop/web-thrift-ui/Icons/Selling';
import { Caption } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './CondensedProductCard.module.css';
import { CondensedProductInfo } from './CondensedProductInfo';
import { CondensedSellerRow } from './CondensedSellerRow';
import { DesktopImageGallery } from './DesktopImageGallery';

import { BuyerActions } from '@/app/[locale]/products/[slug]/components/BuyerActions';
import { BuyerProtectionBanner } from '@/app/[locale]/products/[slug]/components/BuyerProtectionBanner';
import { MediaCarousel } from '@/app/[locale]/products/[slug]/components/Images/MediaCarousel';
import { useCarouselImages } from '@/app/[locale]/products/[slug]/hooks/useCarouselImages';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { useFreeShippingMessagingExperiment } from '@/components/USFreeShippingBanner/useFreeShippingMessagingExperiment';
import { buildStaticUrl } from '@/constants/endpoints';
import {
  getPictureThumbnailUrls,
  getVideoThumbnailUrls,
} from '@/modules/pictures/helpers/getThumbnailUrls';
import { useBespokeCondensedPDP } from '@/modules/product/hooks/useBespokeCondensedPDP';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { getProductSizeName } from '@/modules/sizes/helpers';
import { truncate } from '@/modules/text/truncate';

type Props = {
  onViewDetailsClickAction: () => void;
  estimatedDispatchTime: React.ReactNode;
  nudges: React.ReactNode;
  productBadges: React.ReactNode;
  isCondensedPLAVariantB: boolean;
  isCondensedPLAVariantA: boolean;
};

export function CondensedProductCard({
  onViewDetailsClickAction,
  estimatedDispatchTime,
  productBadges,
  nudges,
  isCondensedPLAVariantB,
  isCondensedPLAVariantA,
}: Props) {
  const { isVariant: isBespokeCondensedPDPVariant } = useBespokeCondensedPDP();
  const commonT = useTranslations('common');
  const productT = useTranslations('product');
  const { data: productBasic } = useProductBySlugBasicPDP();
  const carouselImages = useCarouselImages();
  const { data: productExtended, isLoading: extendedLoading } =
    useProductBySlugExtendedPDP();

  const isInFreeShippingExperiment = useFreeShippingMessagingExperiment(true);

  if (!productBasic) {
    return null;
  }

  const carouselThumbnails = [
    ...getPictureThumbnailUrls(productBasic.pictures),
    ...getVideoThumbnailUrls(productBasic.videos),
  ];
  const attributes = productExtended?.attributes;

  const size =
    productExtended?.sizes &&
    getProductSizeName(
      productExtended.sizes.map((item) => item.name),
      {
        multipleSizes: commonT('MultipleSizes'),
        size: commonT('Size'),
      }
    );

  const inlineAttributes = [
    size,
    attributes?.condition?.name,
    attributes?.brand?.is_known ? attributes.brand?.name : null,
  ].filter(Boolean) as string[];

  const likeOverlay = productExtended ? (
    <div className={styles.likeOverlay}>
      <ProductCardLike
        productId={productBasic.id}
        isLiked={productExtended.is_liked}
        likeCount={productExtended.like_count}
      />
    </div>
  ) : null;

  const resolvedTitle =
    productExtended?.attributes?.product_title || productBasic.description;
  const title = truncate(resolvedTitle, 50);

  return (
    <div
      className={clsx(styles.card, {
        [styles.cardTightSpacing]: isBespokeCondensedPDPVariant,
      })}
    >
      <div className={styles.desktopLayout}>
        <div className={styles.tabletImage}>
          <MediaCarousel
            imageThumbnails={carouselThumbnails}
            productId={productBasic.id}
            accessory={likeOverlay}
            trackImageCarouselIndex={isBespokeCondensedPDPVariant}
          >
            {carouselImages}
          </MediaCarousel>
        </div>

        <DesktopImageGallery
          carouselThumbnails={carouselThumbnails}
          productId={productBasic.id}
          accessory={likeOverlay}
        >
          {carouselImages}
        </DesktopImageGallery>

        <div>
          {nudges}

          <div className={styles.infoSections}>
            <div className={styles.titlePriceGroup}>
              <div
                className={clsx(styles.imageInfoRow, styles.hideCarouselArrows)}
              >
                <MediaCarousel
                  imageThumbnails={carouselThumbnails}
                  productId={productBasic.id}
                  accessory={likeOverlay}
                  trackImageCarouselIndex={isBespokeCondensedPDPVariant}
                >
                  {carouselImages}
                </MediaCarousel>
                <CondensedProductInfo
                  title={title}
                  inlineAttributes={inlineAttributes}
                  extendedLoading={extendedLoading}
                  productBadges={productBadges}
                  onViewDetailsClickAction={onViewDetailsClickAction}
                />
              </div>

              <div className={styles.desktopProductInfo}>
                <CondensedProductInfo
                  title={title}
                  inlineAttributes={inlineAttributes}
                  extendedLoading={extendedLoading}
                  productBadges={productBadges}
                  onViewDetailsClickAction={onViewDetailsClickAction}
                />
              </div>
            </div>

            <div className={styles.buttonsRow}>
              <BuyerActions
                isCondensedPLAVariant={
                  isCondensedPLAVariantA || isCondensedPLAVariantB
                }
              />
            </div>

            <div className={styles.bannerGroup}>
              <BuyerProtectionBanner />
              {extendedLoading ? (
                <>
                  <Placeholder
                    shape={PlaceHolderShape.RECTANGLE}
                    width="40%"
                    height="20px"
                  />
                  <Placeholder
                    shape={PlaceHolderShape.RECTANGLE}
                    width="65%"
                    height="20px"
                  />
                </>
              ) : (
                <>
                  {attributes?.condition && (
                    <div className={styles.infoLine}>
                      {isCondensedPLAVariantB ? (
                        <img
                          src={buildStaticUrl(
                            '/web/assets/sellingHub/graphics/clothes-stack.png'
                          )}
                          alt="Condition"
                          width={48}
                          height={48}
                        />
                      ) : (
                        <span className={styles.infoLineIcon}>
                          <Selling size="md" />
                        </span>
                      )}
                      <Caption variant="lg-regular" colour="base">
                        {attributes.condition.name
                          .toLowerCase()
                          .includes('condition')
                          ? attributes.condition.name
                          : productT('CondensedLayout.Condition', {
                              condition: attributes.condition.name,
                            })}
                      </Caption>
                    </div>
                  )}
                  {estimatedDispatchTime}
                  {isInFreeShippingExperiment && (
                    <div className={styles.infoLine}>
                      {isCondensedPLAVariantB ? (
                        <img
                          src={buildStaticUrl(
                            '/web/assets/illustrations/post-purchase.webp'
                          )}
                          alt="Free shipping"
                          width={48}
                          height={48}
                        />
                      ) : (
                        <span className={styles.infoLineIcon}>
                          <DepopFlameOutline size="md" />
                        </span>
                      )}
                      <Caption variant="lg-regular" colour="base">
                        {productT('CondensedLayout.FreeShipping')}
                      </Caption>
                    </div>
                  )}
                </>
              )}
            </div>

            <CondensedSellerRow />
          </div>
        </div>
      </div>
    </div>
  );
}
